import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import { SxProps, createTheme } from "@mui/material";

//Button
const BUTTON_SHRINK_BY_VALUE = 0.98;

export const spacing = {
  xlDesktop: "96px",
  xlMobile: "64px",
  mediumDesktop: "12px",
  mediumMobile: "12px",
  largeDesktop: "48px",
  largeMobile: "32px",
  regularDesktop: "32px",
  regularMobile: "24px",
  groupingDesktop: "24px",
  groupingMobile: "16px",
  subtitleDesktop: "8px",
  subtitleMobile: "8px",
  smallDesktop: "8px",
  smallMobile: "8px",
  xsDesktop: "4px",
  xsMobile: "4px",
  xxsDesktop: "2px",
  xxsMobile: "2px",
  ctaSpacingDesktop: "16px",
  ctaSpacingMobile: "16px",
  wrapperDesktop: "24px",
  wrapperMobile: "16px",
  small400: "5px",
};

export const palette = {
  white: "#fff",
  offWhite: "#fcfcfc",
  grag: "#555555",
  darkGray: "#666666",
  lightGray: "#dddddd",
  offLightGray: "#e5e5e5",
  green: "#3e6bad",
  lightGreen: "#48a8a5",
  grayBorder: "#f1f1f1",
  /* for background*/
  bgGray: "#f9f9f9",
  /*for tshirt*/
  burgundy: "#501d36",
  cyan: "#2484BA",
  grey: {
    nearBlack: "#111927",
    primary: "#4D5761",
    midGrey: "#E5E7EB",
    lightGrey: "#F3F4F6",
    darkGrey: "#D2D6DB",
    darkerGrey: "#9DA4AE",
    darkBackground: "#F3F4F6",
    lightBackground: "#F9FAFB",
  },
  brand: {
    primary: "#074174",
    darkBlue: "#12213B",
    primaryEffect: "#095190",
    secondary: "#2484BA",
    secondaryButtonHover: "#2892cd",
    pale: "#E2F1FD",
    paleEffect: "#F2F9FF",
    secondaryEffect: "#1F73A2",
    overlay: "rgba(18, 33, 59, 0.2)",
  },
  black: {
    headings: "#222",
    readonlyBg: "#F3F4F6",
    lines: "#F3F4F6",
    readonlyText: "#4D5761",
  },
  red: {
    error: "#D80D0D",
    salmon: "#FF8772",
  },
  previewQuality: {
    poor: "#E60E0E",
    acceptable: "#E69D0E",
    great: "#12A006",
  },
  misc: {
    imgPreviewDark: "#666666",
    imgPreviewLight: "#F1F1F1",
    favorites: "#D61660",
    green: "#05610A",
    warningText: "#7E6607",
    warningBg: "#FEFBEC",
    warningBorder: "#FCF3CF",
    errorText: "#610505",
    errorBg: "#FEECEC",
    errorBorder: "#FCCFCF",
    successText: "#05610A",
    successDot: "#09AE11",
    successBg: "#ECFEED",
    successBorder: "#CFFCD1",
  },
  lightBorderGradient: `linear-gradient(white, white) padding-box,
  linear-gradient(to right, #C8DAE4, #D9C8E4) border-box`,
  borderGradient: `linear-gradient(white, white) padding-box,
    linear-gradient(to right, rgba(84, 172, 222, 1), rgba(169, 84, 222, 1)) border-box`,
  borderGradientHover: `linear-gradient(white, white) padding-box,
    linear-gradient(to right, #C8DAE4, #D9C8E4) border-box`,
};

export const gradient = {
  backgroundGradient:
    "linear-gradient(90deg, rgba(84, 172, 222, 0.04) 0%, rgba(169, 84, 222, 0.04) 100%)",
};

const tempTheme = createTheme();

export const theme = createTheme({
  zIndex: {
    drawer: 1300,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 768,
      lg: 1200,
      xl: 1536,
      xxl: 1920,
    },
  },
  palette: {
    error: {
      main: palette.red.error,
    },
  },
  typography: {
    fontFamily: "Apercu Pro",
    fontSize: 13,
    textXs: {
      fontSize: "10px",
      fontStyle: "normal",
      fontWeight: "400",
      lineHeight: "normal",
      color: palette.grey.primary,
    },
    textXsBold: {
      fontSize: "10px",
      fontStyle: "normal",
      fontWeight: "600",
      lineHeight: "normal",
      color: palette.grey.primary,
    },
    h1: {
      fontSize: "32px",
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "normal",
      color: palette.brand.darkBlue,
      [tempTheme.breakpoints.down("lg")]: {
        fontSize: "20px",
      },
    },
    h2: {
      fontSize: "20px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "normal",
      color: palette.brand.darkBlue,
      [tempTheme.breakpoints.down("lg")]: {
        fontSize: "16px",
      },
    },
    title: {
      fontSize: "24px",
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "normal",
      color: palette.brand.darkBlue,
      [tempTheme.breakpoints.down("lg")]: {
        fontSize: "20px",
      },
    },
    h5: {
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "normal",
      color: palette.brand.darkBlue,
    },
    textRegular: {
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "normal",
      color: palette.grey.nearBlack,
    },
    textRegularBold: {
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "normal",
      color: palette.grey.nearBlack,
    },
    textLarge: {
      fontSize: "18px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "normal",
      color: palette.grey.nearBlack,
    },
    textLargeBold: {
      fontSize: "18px",
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "normal",
      color: palette.grey.nearBlack,
    },
    textSm: {
      fontSize: "12px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "normal",
      color: palette.grey.nearBlack,
    },
    textSmBold: {
      fontSize: "12px",
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "normal",
      color: palette.grey.nearBlack,
    },
    textXxs: {
      fontSize: "8px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "normal",
      color: palette.grey.nearBlack,
    },
    actionBoldBigIcon: {
      fontSize: "16px",
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "24px",
      [tempTheme.breakpoints.down("lg")]: {
        fontWeight: 600,
      },
    },
    actionBigIcon: {
      fontSize: "16px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "24px",
      color: palette.grey.primary,
      [tempTheme.breakpoints.down("lg")]: {
        fontSize: "14px",
      },
    },
    actionSmallIcon: {
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "22px",
      color: palette.grey.nearBlack,
      [tempTheme.breakpoints.down("lg")]: {
        fontSize: "12px",
        lineHeight: "18px !important",
      },
    },
    actionBoldSmallIcon: {
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "22px",
      color: palette.grey.nearBlack,
      [tempTheme.breakpoints.down("lg")]: {
        fontSize: "12px",
        fontWeight: 500,
        lineHeight: "18px !important",
      },
    },
    oldH7: {
      fontSize: "12px",
      fontStyle: "normal",
      fontWeight: "400",
      lineHeight: "normal",
    },
    oldH8: {
      fontSize: "10px",
      fontStyle: "normal",
      fontWeight: "400",
      lineHeight: "normal",
    },
    body1: {
      fontSize: "16px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "normal",
      color: palette.brand.darkBlue,
    },
    body2: {
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "normal",
      color: palette.brand.darkBlue,
    },
  },

  components: {
    MuiInputBase: {
      styleOverrides: {
        root: {
          padding: "8px 12px",
          borderRadius: 8,
          fontSize: 16,
          fontWeight: 400,
          lineHeight: "24px",
          border: `1px solid ${palette.grey.darkGrey}`,
          margin: "1px",
          height: "unset",
          background: palette.white,

          "&>input": {
            padding: 0,
          },
          "&.Mui-disabled": {
            background: palette.black.readonlyBg,
            "&>input": {
              color: `${palette.grey.primary}`,
            },
            "&:hover": {
              boxShadow: "none",
              border: `1px solid ${palette.grey.darkGrey}`,
            },
          },
          "&:hover": {
            boxShadow: "0px 0px 0px 2px rgba(36, 132, 186, 0.10)",
          },
          "&.Mui-focused": {
            borderColor: palette.brand.secondary,
            boxShadow: `0px 0px 0px 1px ${palette.brand.secondary}`,
          },
          "&.Mui-error": {
            border: `1px solid ${palette.red.error}`,
            margin: "1px",
            marginBottom: "4px",
            "&:hover, &.Mui-focused": {
              boxShadow: "0px 0px 0px 2px rgba(216, 13, 13, 0.10)",
            },
            "&>span": {
              marginRight: "0",
            },
          },
        },
        sizeSmall: {
          "&>input": {
            fontSize: "14px",
            lineHeight: "17px",
          },
        },
        input: {
          padding: 0,
          paddingBottom: "1px",
          color: palette.black.headings,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          paddingTop: "8px", // prevents the label from being cut
          "& .MuiInputLabel-root": {
            color: "#4D5761",
            transform: "translate(14px, -7px) scale(0.75)",
          },
          "& .MuiInputLabel-root:not(.MuiFormLabel-filled)": {
            transform: "translate(14px, 11px) scale(1)",
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: palette.brand.secondary,
            transform: "translate(14px, -7px) scale(0.75)",
          },
          "& .MuiButtonBase-root": {
            padding: 0,
          },
        },
        inputRoot: {
          maxHeight: "unset",
          border: "none !important",
          fontSize: "16px",
          borderRadius: "8px",
          padding: 0,
          paddingRight: "12px",
          margin: 0,

          [tempTheme.breakpoints.down("lg")]: {
            fontSize: "14px",
          },

          ".MuiOutlinedInput-notchedOutline": {
            borderColor: palette.grey.midGrey,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: palette.grey.midGrey,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: palette.brand.secondary,
          },
          "&>input": {
            padding: "8px 0px 8px 12px !important",
            fontWeight: 400,
            lineHeight: "24px",
            height: "24px",
          },
          "&.Mui-focused": {
            border: "none",
            boxShadow: "none",
          },
          "&:hover": {
            boxShadow: "none",
          },
          "&:active": {
            border: "none",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        text: {
          padding: "0 !important",
          color: `${palette.brand.secondary} !important`,
          "&:hover": {
            textDecoration: "underline",
          },
        },
        sizeSmall: {
          fontSize: 12,
          lineHeight: "16px",
          padding: "8px 12px",
        },
        sizeMedium: {
          fontSize: 16,
          lineHeight: "24px",
          padding: "12px 20px",
          "&:active": {
            transform: `scale(${BUTTON_SHRINK_BY_VALUE}, ${BUTTON_SHRINK_BY_VALUE})`,
          },
        },
        sizeLarge: {
          fontSize: 16,
          lineHeight: "24px",
          padding: "12px 24px",
          fontWeight: 600,
          "&:active": {
            transform: `scale(${BUTTON_SHRINK_BY_VALUE}, ${BUTTON_SHRINK_BY_VALUE})`,
          },
        },
        root: {
          borderRadius: 8,
          maxHeight: 48,
          textTransform: "none",
          boxShadow: "none !important",
          "&:hover": {
            boxShadow: "none",
          },
        },
        containedPrimary: {
          backgroundColor: palette.brand.primary,
          color: palette.white,
          fontWeight: 600,

          "&:hover": {
            backgroundColor: palette.brand.primaryEffect,
          },
          "&.Mui-disabled": {
            background: "#ddd",
            color: "#858585",
          },
        },
        containedSecondary: {
          backgroundColor: palette.brand.secondary,
          color: palette.white,
          padding: "8px 12px",
          maxHeight: 36,
          fontSize: 12,
          fontWeight: 500,
          maxWidth: "fit-content",

          "&:hover": {
            backgroundColor: palette.brand.secondaryButtonHover,
          },
        },
        textPrimary: {
          background: "transparent",
          color: palette.brand.primary,
          fontWeight: 500,
          fontSize: "12px !important",
          "&:hover": {
            backgroundColor: "transparent",
          },
        },
        outlined: {
          borderColor: palette.brand.primary,
          color: palette.brand.primary,
          "&:hover": {
            borderColor: palette.brand.primary,
            backgroundColor: palette.brand.paleEffect,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: `${palette.grey.nearBlack} !important`,
          "&.Mui-checked": {
            color: `${palette.brand.secondary} !important`,
          },
        },
        sizeSmall: {
          height: "18px",
          width: "18px",
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontFamily: "Apercu Pro",
          fontSize: "14px",
          fontWeight: 400,
          color: palette.darkGray,
          lineHeight: "normal",
          "&.Mui-error": {
            fontSize: "12px",
            fontWeight: 400,
            lineHeight: "150%",
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        fontSizeMedium: {
          width: "20px",
          height: "20px",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: "4px",
          padding: "2px",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: palette.brand.pale,
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          gap: "4px",
          display: "flex",
          flexDirection: "column",
          "::before": {
            display: "none",
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        content: {
          gap: "10px",
          display: "flex",
          alignItems: "center",
        },
        expandIconWrapper: {
          transform: "none !important",
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          gap: "8px",
          display: "flex",
          flexDirection: "column",
          padding: "0px 8px",
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          paddingRight: "16px",
          paddingLeft: "16px",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paperWidthMd: {
          maxWidth: 720,
          [tempTheme.breakpoints.down("lg")]: {
            maxWidth: "100%",
          },
        },
        paper: {
          boxShadow: "none",
          borderRadius: "8px",
          padding: spacing.wrapperDesktop,
          [tempTheme.breakpoints.down("lg")]: {
            padding: `${spacing.wrapperMobile} ${spacing.wrapperMobile} ${spacing.xlMobile}`,
            borderRadius: "0px",
            overflowY: "auto",
            maxHeight: "100vh",
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: "0px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: spacing.regularDesktop,
          [tempTheme.breakpoints.down("lg")]: {
            marginBottom: spacing.regularMobile,
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "0px",
          overflow: "hidden",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          marginTop: "24px",
          padding: 0,
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: "8px",
          boxShadow: "0px 1px 16px 0px rgba(16, 24, 40, 0.08)",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: "8px 12px",
          display: "flex",
          justifyContent: "space-between",
          fontSize: 14,
          fontWeight: 400,
          lineHeight: "18px",
          "&.Mui-selected": {
            background: "unset",
            "&:hover": {
              background: palette.brand.paleEffect,
            },
            "&:active": {
              background: palette.brand.pale,
            },
          },
          "&:active": {
            background: palette.brand.pale,
          },
          "&:hover": {
            background: palette.brand.paleEffect,
            "&:active": {
              background: palette.brand.pale,
            },
          },
          "&.Mui-focusVisible": {
            background: "unset",
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: palette.grey.lightGrey,
          opacity: 1,
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        root: {
          minWidth: "fit-content",
        },
        badge: {
          padding: "0px 3px",
          fontSize: 9,
          lineHeight: "normal",
          fontWeight: 500,
          maxHeight: 10,
          verticalAlign: "center",
          textAlign: "center",
          borderRadius: "8px",
          minWidth: "fit-content",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderTopLeftRadius: "12px",
          borderTopRightRadius: "12px",
          paddingTop: spacing.wrapperMobile,
          paddingBottom: spacing.wrapperMobile,
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          // background: "blue",
          background: palette.grey.lightBackground,
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: KeyboardArrowDown,
      },
      styleOverrides: {
        select: {
          padding: "8px 12px",
          border: `none`,
        },
        root: {
          padding: "0",
          borderRadius: 8,
          fontSize: 16,
          fontWeight: 400,
          lineHeight: "24px",
          margin: "0px",
          height: "unset",
          border: `none`,
          "&.Mui-focused": {
            border: `none`,
            boxShadow: "0px 0px 0px 2px #2484BA",
          },
          "&:hover": {
            boxShadow: "0px 0px 0px 2px rgba(36, 132, 186, 0.10)",
          },
          "&.readonly": {
            boxShadow: "none",
            pointerEvents: "none",
            "& .MuiSelect-icon": {
              display: "none",
            },
          },
          "&.Mui-disabled:hover": {
            border: "none",
            boxShadow: "none",
          },
          "&.Mui-disabled": {
            border: "none",
            boxShadow: "none",
          },
          "&:active": {
            border: `none`,
          },
          ".MuiOutlinedInput-notchedOutline": {
            borderColor: palette.grey.darkGrey,
            padding: 0,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: palette.grey.darkGrey,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: palette.brand.secondary,
            borderWidth: 0,
          },
        },
        icon: {
          color: palette.grey.nearBlack,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          whiteSpace: "pre-line",
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          color: palette.brand.secondary,
          padding: "0 !important",
          "& .MuiSlider-thumb": {
            width: 16,
            height: 16,
            backgroundColor: "#fff",
            border: `4px solid ${palette.brand.secondary}`,
            boxShadow: "none",
            "&:hover, &.Mui-focusVisible": {
              boxShadow: "none",
            },
          },
          "& .MuiSlider-rail": {
            height: 8,
            backgroundColor: palette.grey.lightBackground,
            opacity: 1,
            border: `1px solid ${palette.grey.midGrey}`,
          },
          "& .MuiSlider-track": {
            height: 8,
            backgroundColor: palette.brand.secondary,
          },
          "& .MuiSlider-mark": {
            width: 1,
            height: 8,
            backgroundColor: palette.grey.midGrey,
            "&.MuiSlider-markActive": {
              backgroundColor: "transparent",
            },
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          border: `1px solid ${palette.grey.midGrey}`,
          "&::-webkit-scrollbar": {
            width: "6px",
            height: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#D2D6DB",
            borderRadius: "4px",
            "&:hover": {
              background: "#D2D6DB",
            },
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: "collapse",
          width: "100%",
          backgroundColor: palette.white,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-root": {
            borderBottom: `1px solid ${palette.grey.midGrey}`,
            fontWeight: 600,
            color: palette.grey.primary,
            background: "#fbfbfb",
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-root": {
            borderBottom: `1px solid ${palette.grey.lightGrey}`,
            color: palette.grey.nearBlack,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "16px",
          fontSize: "14px",
          lineHeight: "20px",
        },
      },
    },
    MuiTableFooter: {
      styleOverrides: {
        root: {
          borderTop: `1px solid ${palette.grey.midGrey}`,
          background: palette.white,
          position: "sticky",
          bottom: 0,
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        toolbar: {
          alignItems: "center !important",
          "& > *": {
            marginTop: 0,
            marginBottom: 0,
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 28,
          height: 14,
          padding: 0,
          margin: 0,
          "& .MuiSwitch-switchBase": {
            padding: 0,
            margin: 2,
            transform: "translateY(0)", // Center the thumb vertically
            transitionDuration: "300ms",
            "&.Mui-checked": {
              transform: "translateX(14px)", // Maintain vertical centering when checked
              color: "#fff",
              "& + .MuiSwitch-track": {
                backgroundColor: palette.brand.secondary,
                opacity: 1,
                border: 0,
              },
              "&.Mui-disabled + .MuiSwitch-track": {
                opacity: 0.5,
              },
            },
            "&.Mui-focusVisible .MuiSwitch-thumb": {
              color: palette.brand.secondary,
              border: "6px solid #fff",
            },
            "&.Mui-disabled .MuiSwitch-thumb": {
              color: palette.grey.lightGrey,
            },
            "&.Mui-disabled + .MuiSwitch-track": {
              opacity: 0.7,
            },
          },
          "& .MuiSwitch-thumb": {
            boxSizing: "border-box",
            boxShadow: "none",
            width: 10,
            height: 10,
          },
          "& .MuiSwitch-track": {
            borderRadius: 14 / 2,
            backgroundColor: palette.grey.darkGrey,
            opacity: 1,
            transition: "background-color 500ms",
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: "0px 4px 0px 9px",
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: palette.brand.secondary,
        },
        scrollButtons: {
          "& .MuiButtonBase-root": {
            padding: "0px 16px",
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          padding: "12px",
          "&.Mui-selected": {
            fontWeight: 600,
            color: `${palette.grey.nearBlack} !important`,
          },
        },
      },
    },
  },
});

export const TextGradientStyles = {
  background: "linear-gradient(90deg, #54ACDE 0%, #A954DE 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
};

// // Example usage
// const styles = createStyles({
//   container: { /* your styles here */ },
//   title: { /* your styles here */ },
// });
export type Styles = { [key: string]: SxProps };
export const createStyles = <T extends Styles>(styles: T): T => styles;
// export const createStyles = <T extends Styles>(
//   styles: T | ((...args: any) => T)
// ): T | ((...args: any) => T) => (typeof styles === "function" ? styles : styles);
